import { fDateIso, fDateLong } from '@faks/shared';
import { yupResolver } from '@hookform/resolvers/yup';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { LoadingButton } from '@mui/lab';
import { Box, Button, Card, Stack, Typography, useTheme } from '@mui/material';
import { useIndexQuery, useUpdatePharmacyMutation } from 'api';
import { Banner } from 'components/banner/banner';
import { CenteredSpinner } from 'components/CenteredSpinner';
import { FormProvider, RHFSelectField, TextField } from 'components/hook-form';
import { CheckboxWithLabel } from 'components/hook-form/CheckboxWithLabel';
import { CheckRoundedIcon } from 'components/icons/CheckRoundedIcon';
import { InfoIconMUI2 } from 'components/icons/InfoIconMUI2';
import { StyledLink } from 'components/link/styles';
import { BasicModal } from 'components/modals/BasicModal';
import { useCurrentPharmacyOrThrow } from 'hooks/slices';
import { startCase } from 'lodash';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import type { SetPanelistConnectionParams } from 'types/api/pharmacies/panelist_connection';
import type { Panelist } from 'types/models/pharmacy_lgo_settings';
import { LGO_APPLICATION_DATE, PANELISTS } from 'utils/lgoUtils';
import { LGO_LINKS, PRIVACY_POLICY_URL } from 'utils/linkUtils';
import { cipValidator, requiredString } from 'utils/yupValidators';
import * as Yup from 'yup';

type ConnectLgoFormValues = {
  panelist: Panelist;
  areConditionsAccepted: boolean;
  cip: string;
};

type Props = {
  handleOnSet: (panelistConnectionParams: SetPanelistConnectionParams) => void;
  disableConnectButton: boolean;
  isLoading: boolean;
};

export const ConnectPanelistCard = ({ handleOnSet, disableConnectButton, isLoading }: Props) => {
  const theme = useTheme();
  const pharmacy = useCurrentPharmacyOrThrow();
  const [isConfirmCIPModalOpen, setIsConfirmCIPModalOpen] = useState(false);

  const updatePharmacy = useUpdatePharmacyMutation();

  const myGroupQuery = useIndexQuery('pharmacies/my_group', {});
  const pharmacyGroup = myGroupQuery.data?.group;

  const suggestedPanelist = pharmacyGroup?.suggested_panelist;

  const defaultFormValues: ConnectLgoFormValues = {
    panelist: suggestedPanelist || 'ospharm',
    areConditionsAccepted: false,
    cip: pharmacy.cip || '',
  };

  const doSubmit = () => {
    const pharmacyPanelistConnectionParams = {
      panelist: connectLgoFormValues.panelist,
      accepted_text: textThatMustBeAccepted,
      application_date: fDateIso(LGO_APPLICATION_DATE),
    };

    handleOnSet({
      pharmacy_lgo_setting: pharmacyPanelistConnectionParams,
    });
  };

  const ConnectLgoFormSchema = Yup.object().shape({
    panelist: requiredString(),
    areConditionsAccepted: Yup.boolean() .test('areConditionsAccepted', (value) => value === true),
  });

  const CheckCipFormSchema = Yup.object().shape({
    cip: cipValidator('cip').required(t`Veuillez renseigner ce champ`),
  });

  const methods = useForm<ConnectLgoFormValues>({
    resolver: yupResolver(ConnectLgoFormSchema.concat(CheckCipFormSchema)),
    defaultValues: defaultFormValues,
  });

  const { handleSubmit, watch, reset } = methods;

  const connectLgoFormValues = watch();

  // biome-ignore lint/correctness/useExhaustiveDependencies: -
  useEffect(() => {
    reset(defaultFormValues);
  }, [reset, defaultFormValues.panelist]);

  const onSubmit = () => {
    updatePharmacy
      .mutateAsync({
        pharmacy: { id: pharmacy.id, cip: connectLgoFormValues.cip },
      })
      .then(doSubmit);
  };

  const textThatMustBeAccepted =
    connectLgoFormValues.panelist === 'santestat'
      ? t`Je certifie que je suis le titulaire de la pharmacie et j’autorise ${startCase(connectLgoFormValues.panelist)} à transmettre mes données de vente à Faks à partir du ${fDateLong(LGO_APPLICATION_DATE)}, conformément à la Politique de confidentialité de Faks et selon les Conditions Générales de Santestat.`
      : t`Je certifie que je suis le titulaire de la pharmacie et j’autorise ${startCase(connectLgoFormValues.panelist)} à transmettre mes données de vente à Faks à partir du ${fDateLong(LGO_APPLICATION_DATE)}, conformément à la Politique de confidentialité de Faks.`;

  if (myGroupQuery.isPending) return <CenteredSpinner />;

  return (
    <FormProvider methods={methods}>
      <Card sx={{ p: '25px' }}>
        <Typography fontWeight="bold" color={theme.palette.grey[500]}>
          {t`CHOISISSEZ VOTRE EXTRACTEUR`}
        </Typography>
        {suggestedPanelist && (
          <Banner
            icon={<InfoIconMUI2 fontSize="medium" />}
            title={t`${suggestedPanelist.toUpperCase()} est l’extracteur conseillé par votre groupement`}
            sx={{ mt: 2 }}
          />
        )}
        <Stack gap={2} marginTop={3}>
          <RHFSelectField
            name="panelist"
            options={PANELISTS.map((panelist) => ({
              value: panelist,
              title: panelist.toUpperCase(),
            }))}
            label={t`Sélectionnez votre extracteur`}
            sx={{ maxWidth: { lg: '20%' } }}
          />

          {connectLgoFormValues.panelist !== 'santestat' && (
            <Typography fontStyle="italic">
              {t`Si vous n’avez pas encore de compte chez cet extracteur, `}{' '}
              <StyledLink
                href={LGO_LINKS.ONBOARDINGS[connectLgoFormValues.panelist]}
                target="_blank"
              >
                {t`cliquez ici pour créer un compte ${startCase(connectLgoFormValues.panelist)}`}
              </StyledLink>
            </Typography>
          )}

          <Banner
            icon={<CheckRoundedIcon fontSize="medium" sx={{ marginRight: 1 }} />}
            title={
              <>
                <Box>{t`Faks ne commercialise pas vos données de vente.`}</Box>
                <StyledLink href={LGO_LINKS.INTERCOM} target="_blank">
                  {t`En savoir plus sur les engagements de Faks.`}
                </StyledLink>
              </>
            }
            color="success"
            sx={{ maxWidth: { lg: '40%' } }}
          />

          <CheckboxWithLabel
            name="areConditionsAccepted"
            label={
              <Typography fontWeight="medium">
                <Trans>
                  Je certifie que je suis le titulaire de la pharmacie et j’autorise{' '}
                  {startCase(connectLgoFormValues.panelist)} à transmettre mes données de vente à
                  Faks à partir du {fDateLong(LGO_APPLICATION_DATE)}, conformément à la{' '}
                  <StyledLink href={PRIVACY_POLICY_URL} target="_blank">
                    Politique de confidentialité de Faks
                  </StyledLink>
                </Trans>
                {connectLgoFormValues.panelist === 'santestat' && (
                  <>
                    {' '}
                    <Trans>
                      et selon les{' '}
                      <StyledLink
                        href="https://storage.googleapis.com/faks-public-assets/CGU_SANTESTAT_0723.pdf"
                        target="_blank"
                      >
                        Conditions Générales de Santestat
                      </StyledLink>
                    </Trans>
                  </>
                )}
              </Typography>
            }
          />

          <Box display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              disabled={disableConnectButton}
              onClick={() =>
                ConnectLgoFormSchema.validate(connectLgoFormValues)
                  .then(() => setIsConfirmCIPModalOpen(true))
                  .catch((error) => {
                    if (error.message.includes('areConditionsAccepted')) {
                      toast.error(t`Oops ! Vous devez accepter les conditions.`);
                    } else {
                      throw error;
                    }
                  })
              }
            >
              {t`Confirmer`}
            </Button>
          </Box>
        </Stack>
      </Card>

      <BasicModal
        open={isConfirmCIPModalOpen}
        onClose={() => setIsConfirmCIPModalOpen(false)}
        title={t`Vérification du CIP`}
      >
        <Typography
          my={2}
        >{t`Veuillez vérifier le CIP de votre pharmacie afin que la connexion avec votre extracteur soit correctement activée.`}</Typography>
        <TextField name="cip" label={t`CIP de la pharmacie`} placeholder={pharmacy.cip || ''} />
        <Box display="flex" justifyContent="flex-end">
          <LoadingButton
            onClick={handleSubmit(onSubmit)}
            loading={updatePharmacy.isPending || isLoading}
            variant="contained"
            sx={{ mt: 2 }}
          >
            {t`Confirmer`}
          </LoadingButton>
        </Box>
      </BasicModal>
    </FormProvider>
  );
};
